import React, { useEffect, useMemo, useRef, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  FALL_CRAWL_GIVEAWAY_STAMP_THRESHOLD,
  FALL_CRAWL_PASSPORT_CLAIM_RADIUS_METERS,
  FALL_CRAWL_PASSPORT_COUNTIES,
  canCollectFallCrawlPassportStamps,
  collectFallCrawlPassportStop,
  createEmptyFallCrawlPassportState,
  getFallCrawlGiveawayStatus,
  getFallCrawlCountyProgress,
  getFallCrawlPassportProgress,
  getFallCrawlPassportStops,
  isFallCrawlPassportEvent,
  isFallCrawlPassportIdentity,
  isWithinFallCrawlPassportRadius,
  loadFallCrawlPassportState,
  removeFallCrawlPassportStop,
  resetFallCrawlPassportState,
  normalizeFallCrawlGiveawayEmail,
  normalizeFallCrawlGiveawayPhone,
  saveFallCrawlPassportState,
} from "../../utils/fallCrawlPassport";
import bedfordBadge from "../../assets/fall-crawl-2026-passport-badge-stamps/bedford-county-complete.png";
import coffeeBadge from "../../assets/fall-crawl-2026-passport-badge-stamps/coffee-county-complete.png";
import confettiOverlay from "../../assets/fall-crawl-2026-passport-badge-stamps/fall-crawl-confetti.png";
import countyCompleteBanner from "../../assets/fall-crawl-2026-passport-badge-stamps/fall-crawl-county-complete-banner.png";
import passportCover from "../../assets/fall-crawl-2026-passport-badge-stamps/fall-crawl-passport-cover.png";
import passportHeader from "../../assets/fall-crawl-2026-passport-badge-stamps/fall-crawl-passport-header.png";
import passportPageBg from "../../assets/fall-crawl-2026-passport-badge-stamps/fall-crawl-passport-page-bg.png";
import routeCompleteBadge from "../../assets/fall-crawl-2026-passport-badge-stamps/fall-crawl-route-complete.png";
import stampCollectedBanner from "../../assets/fall-crawl-2026-passport-badge-stamps/fall-crawl-stamp-collected-banner.png";
import genericStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/fall-crawl-stamp-generic.png";
import lockedStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/fall-crawl-stamp-locked.png";
import normandyStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/normandy-farmers-market-stamp.png";
import threeHFarmsStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Host-Stamps/3H-farms.png";
import andertonFarmsStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Host-Stamps/anderton-farms.png";
import coffeeCountyFairgroundsStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Host-Stamps/coffee-county-fairgrounds.png";
import goodFriendsMusicFestStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Host-Stamps/good-friends-music-fest.png";
import manchesterCommunityMarketStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Host-Stamps/manchester-community-market.png";
import theCelticCupStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Host-Stamps/the-celtic-cup(1).png";
import tobinFarmStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Host-Stamps/tobinfarm.png";
import allensAbideMercantileStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/allens-abide-mercantile.png";
import claraMaeFarmStandStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/clara-mae-s-farm-stand.png";
import coneflowerCornerFarmStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/coneflower-corner-farm.png";
import fallAtFrazierStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/fall-at-frazier.png";
import farmAndFlourStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/farm-and-flour.png";
import feathersFlourFloraStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/feathers-flour-flora.png";
import floodComfortBakeryStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/flood-comfort-bakery.png";
import flourPowerStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/flour-power.png";
import ginasCountryKitchenStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/ginas-country-kitchen.png";
import highway130Stamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/highway-130.png";
import kcCottageStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/K&C-cottage.png";
import kikisFlourShoppeStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/kikis-flour-shoppe-and-farm-stand.png";
import layLaysFarmStandStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/lay-lays-farm-stand.png";
import nativeFareFarmsStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/native-fare-farms.png";
import rollingDoughFarmStandStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/rolling-dough-farm-stand.png";
import southernForeverStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/southern-forever.png";
import sugarbloomsStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/sugarblooms.png";
import tennesseeBakingCompanyStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/tennessee-baking-company.png";
import theFarmstandAtRausStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/the-farmstand-at-raus.png";
import theFarmyardMarketStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/the-farmyard-market.png";
import theLittleFarmStandStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/the-little-farm-stand.png";
import theSprinkleSocietyStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/the-sprinkle-society.png";
import theStandOnIrisDriveStamp from "../../assets/fall-crawl-2026-passport-badge-stamps/Normal-stops/the-stand-on-iris-drive.png";
import { trackFallCrawlInteraction } from "../Analytics/FallCrawlInteractionAnalytics";
import "./LocalLive.css";

const PASSPORT_ASSETS = {
  bedfordBadge,
  coffeeBadge,
  confettiOverlay,
  countyCompleteBanner,
  cover: passportCover,
  header: passportHeader,
  pageBg: passportPageBg,
  routeCompleteBadge,
  stampCollectedBanner,
  genericStamp,
  lockedStamp,
  normandyStamp,
  threeHFarmsStamp,
  andertonFarmsStamp,
  coffeeCountyFairgroundsStamp,
  goodFriendsMusicFestStamp,
  manchesterCommunityMarketStamp,
  theCelticCupStamp,
  tobinFarmStamp,
  allensAbideMercantileStamp,
  claraMaeFarmStandStamp,
  coneflowerCornerFarmStamp,
  fallAtFrazierStamp,
  farmAndFlourStamp,
  feathersFlourFloraStamp,
  floodComfortBakeryStamp,
  flourPowerStamp,
  ginasCountryKitchenStamp,
  highway130Stamp,
  kcCottageStamp,
  kikisFlourShoppeStamp,
  layLaysFarmStandStamp,
  nativeFareFarmsStamp,
  rollingDoughFarmStandStamp,
  southernForeverStamp,
  sugarbloomsStamp,
  tennesseeBakingCompanyStamp,
  theFarmstandAtRausStamp,
  theFarmyardMarketStamp,
  theLittleFarmStandStamp,
  theSprinkleSocietyStamp,
  theStandOnIrisDriveStamp,
};

const LOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
};
const GIVEAWAY_PROMPT_DELAY_MS = 1700;

const normalizeStampLookupText = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const APPROVED_STAMP_ASSET_BY_LOOKUP = {
  "3h farms": "threeHFarmsStamp",
  "anderton farms": "andertonFarmsStamp",
  "coffee county fairgrounds": "coffeeCountyFairgroundsStamp",
  "coffee county fairgrounds ag swap": "coffeeCountyFairgroundsStamp",
  "coffee county fair ag and handmade swap": "coffeeCountyFairgroundsStamp",
  "coffee county fair ag handmade swap": "coffeeCountyFairgroundsStamp",
  "good friends music fest": "goodFriendsMusicFestStamp",
  "manchester community market": "manchesterCommunityMarketStamp",
  "the celtic cup": "theCelticCupStamp",
  "celtic cup coffee house": "theCelticCupStamp",
  "the celtic cup coffee house": "theCelticCupStamp",
  "tobin farm": "tobinFarmStamp",
  tobinfarm: "tobinFarmStamp",
  "allens abide mercantile": "allensAbideMercantileStamp",
  "allen s abide mercantile": "allensAbideMercantileStamp",
  "clara mae s farm stand": "claraMaeFarmStandStamp",
  "clara maes farm stand": "claraMaeFarmStandStamp",
  "coneflower corner farm": "coneflowerCornerFarmStamp",
  "fall at frazier": "fallAtFrazierStamp",
  "farm and flour": "farmAndFlourStamp",
  "feathers flour and flora": "feathersFlourFloraStamp",
  "flood comfort bakery": "floodComfortBakeryStamp",
  "flour power": "flourPowerStamp",
  "gina s country kitchen": "ginasCountryKitchenStamp",
  "ginas country kitchen": "ginasCountryKitchenStamp",
  "highway 130": "highway130Stamp",
  "highway 130 farm stand": "highway130Stamp",
  "k and c cottage": "kcCottageStamp",
  "kc cottage": "kcCottageStamp",
  "kiki s flour shoppe": "kikisFlourShoppeStamp",
  "kikis flour shoppe": "kikisFlourShoppeStamp",
  "kikis flour shoppe and farm stand": "kikisFlourShoppeStamp",
  "lay lay s farm stand": "layLaysFarmStandStamp",
  "lay lays farm stand": "layLaysFarmStandStamp",
  "native fare farms": "nativeFareFarmsStamp",
  "rolling dough farm stand": "rollingDoughFarmStandStamp",
  "southern forever": "southernForeverStamp",
  sugarblooms: "sugarbloomsStamp",
  "sugarblooms tn": "sugarbloomsStamp",
  "tennessee baking company": "tennesseeBakingCompanyStamp",
  "farmstand at raus": "theFarmstandAtRausStamp",
  "the farmstand at raus": "theFarmstandAtRausStamp",
  "the farmyard market": "theFarmyardMarketStamp",
  "the little farm stand": "theLittleFarmStandStamp",
  "sprinkle society": "theSprinkleSocietyStamp",
  "the sprinkle society": "theSprinkleSocietyStamp",
  "the stand on iris drive": "theStandOnIrisDriveStamp",
};

const HOST_STAMP_ASSET_KEYS = new Set([
  "threeHFarmsStamp",
  "andertonFarmsStamp",
  "coffeeCountyFairgroundsStamp",
  "goodFriendsMusicFestStamp",
  "manchesterCommunityMarketStamp",
  "theCelticCupStamp",
  "tobinFarmStamp",
]);

const SPECIAL_STAMP_ASSET_KEYS = new Set([
  "normandyStamp",
]);

const getStampLookupValues = (passportStop = {}) => [
  passportStop.stampAssetKey,
  passportStop.passportStampKey,
  passportStop.passportStampAssetKey,
  passportStop.stopId,
  passportStop.displayName,
  passportStop.stop?.passportStampKey,
  passportStop.stop?.passportStampAssetKey,
  passportStop.stop?.stampAssetKey,
  passportStop.stop?.passportDisplayName,
  passportStop.stop?.name,
  passportStop.stop?.displayName,
  passportStop.stop?.title,
].filter(Boolean);

export const getFallCrawlPassportStampAssetKey = (passportStop = {}) => {
  const lookupValues = getStampLookupValues(passportStop).map(normalizeStampLookupText);
  if (lookupValues.some((value) => value.includes("normandy farmers market") ||
    (value.includes("historic town") && value.includes("normandy")) ||
    value === "normandy")) {
    return "normandyStamp";
  }
  return lookupValues.map((value) => APPROVED_STAMP_ASSET_BY_LOOKUP[value]).find(Boolean) || "genericStamp";
};

export const getFallCrawlPassportStampResolution = (passportStop = {}) => {
  const assetKey = getFallCrawlPassportStampAssetKey(passportStop);
  const asset = PASSPORT_ASSETS[assetKey] || PASSPORT_ASSETS.genericStamp;
  const fallback = assetKey === "genericStamp" || !PASSPORT_ASSETS[assetKey];
  const stampType = fallback
    ? "FALLBACK / MISSING"
    : HOST_STAMP_ASSET_KEYS.has(assetKey)
      ? "HOST STAMP"
      : SPECIAL_STAMP_ASSET_KEYS.has(assetKey)
        ? "SPECIAL STAMP"
        : "NORMAL STAMP";

  return {
    asset,
    assetKey,
    assetPath: typeof asset === "string" ? asset : "",
    fallback,
    stampType,
  };
};

const getStopStampLabel = (passportStop) =>
  getFallCrawlPassportStampAssetKey(passportStop) === "normandyStamp"
    ? "Normandy Farmers Market"
    : "Official Stop";

export const getFallCrawlPassportStampAsset = (passportStop) =>
  PASSPORT_ASSETS[getFallCrawlPassportStampAssetKey(passportStop)] || PASSPORT_ASSETS.genericStamp;

const getCountyBadgeAsset = (countyKey) =>
  countyKey === "coffee" ? PASSPORT_ASSETS.coffeeBadge : PASSPORT_ASSETS.bedfordBadge;

const formatCollectedAt = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const getPositionOnce = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation?.getCurrentPosition) {
      reject(new Error("Location is unavailable on this device."));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, LOCATION_OPTIONS);
  });

const getLocationErrorMessage = (error = {}) => {
  if (error?.code === 1) {
    return "Location permission is blocked. You can still browse the route and try again after allowing location access.";
  }
  if (error?.code === 2) {
    return "Your device could not determine your location. You can still browse the route and try again near the stop.";
  }
  if (error?.code === 3) {
    return "Location timed out. Move where your device has signal and check again.";
  }
  return "Location could not be checked on this device. You can still browse the Backroads Book and try again.";
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const FallCrawlProgressBar = ({ progress, isUpdating = false }) => (
  <div className={`fall-crawl-passport-progress ${isUpdating ? "is-updating" : ""}`} aria-label={progress.label}>
    <div className="fall-crawl-passport-progress-track">
      <span style={{ width: `${progress.percent}%` }} />
    </div>
    <p>{progress.label}</p>
  </div>
);

const FallCrawlPassportStamp = ({
  passportStop,
  collectedAt,
  canCollect,
  busy,
  isNew,
  nearby,
  locationAvailable,
  locationError,
  onCheckLocation,
  onClaim,
}) => {
  const collected = Boolean(collectedAt);
  const actionLabel = locationError
    ? "Check my location again"
    : locationAvailable
      ? "Check my location again"
      : "Check my location";
  return (
    <article className={`fall-crawl-passport-stamp ${collected ? "collected" : "locked"} ${isNew ? "just-collected" : ""}`}>
      <div className="fall-crawl-passport-stamp-art" aria-label={`${passportStop.displayName} ${collected ? "collected" : "not collected yet"}`}>
        {collected ? (
          <img
            className="fall-crawl-passport-stamp-image"
            src={getFallCrawlPassportStampAsset(passportStop)}
            alt={`${getStopStampLabel(passportStop)} stamp collected for ${passportStop.displayName}`}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <img
            className="fall-crawl-passport-stamp-image locked"
            src={PASSPORT_ASSETS.lockedStamp}
            alt={`${passportStop.displayName} stamp not collected yet`}
            loading="lazy"
            decoding="async"
          />
        )}
      </div>
      <div>
        <h4>{passportStop.displayName}</h4>
        {passportStop.town && <p>{passportStop.town}</p>}
        {collected ? (
          <p className="fall-crawl-passport-collected">Collected {formatCollectedAt(collectedAt)}</p>
        ) : !canCollect ? (
          <p>The Backroads Book: Fall Crawl 2026 stamp collection is not live yet.</p>
        ) : !passportStop.coordinates ? (
          <p>This stop cannot currently be location-verified. Please check with the event host.</p>
        ) : locationError ? (
          <p>{locationError}</p>
        ) : !locationAvailable ? (
          <p>Check your location to unlock this official stop stamp.</p>
        ) : nearby ? (
          <p>You are close enough to claim this official stop stamp.</p>
        ) : (
          <p>Get closer to this stop to unlock its stamp.</p>
        )}
      </div>
      {!collected && canCollect && passportStop.coordinates && nearby && (
        <button
          type="button"
          className="fall-crawl-passport-action"
          disabled={busy}
          onClick={() => onClaim(passportStop)}
        >
          {busy ? "Checking..." : "Claim Stamp"}
        </button>
      )}
      {!collected && canCollect && passportStop.coordinates && !nearby && (
        <button
          type="button"
          className="fall-crawl-passport-action secondary"
          disabled={busy}
          onClick={onCheckLocation}
        >
          {busy ? "Checking..." : actionLabel}
        </button>
      )}
    </article>
  );
};

const FallCrawlCountyBadge = ({ county, isNew }) => (
  <article className={`fall-crawl-passport-county ${county.complete ? "complete" : "locked"} ${isNew ? "just-unlocked" : ""}`}>
    <img
      className="fall-crawl-passport-county-badge"
      src={getCountyBadgeAsset(county.key)}
      alt={`${county.label} Fall Crawl 2026 ${county.complete ? "complete" : "completion badge locked"}`}
      loading="lazy"
      decoding="async"
    />
    <p>{county.complete ? `${county.label.toUpperCase()} COMPLETE!` : `${county.collectedCount} of ${county.totalStops} stops`}</p>
  </article>
);

const getRewardMessage = (result, previousState, nextState) => {
  if (!result?.awarded) return null;
  const previousBadges = new Set(previousState.unlockedCountyBadges || []);
  const newCounty = (nextState.unlockedCountyBadges || []).find((key) => !previousBadges.has(key));
  if (nextState.routeComplete && !previousState.routeComplete) {
    return {
      type: "route",
      title: "THE BACKROADS BOOK ROUTE COMPLETE!",
      text: "You collected every required official stop stamp.",
    };
  }
  if (newCounty) {
    const label = FALL_CRAWL_PASSPORT_COUNTIES[newCounty]?.label || "County";
    return {
      type: "county",
      title: `${label.toUpperCase()} COMPLETE!`,
      text: "You unlocked a county completion badge.",
    };
  }
  return {
    type: "stamp",
    title: "STAMP COLLECTED",
    text: `The Backroads Book now includes ${result.stop.displayName}.`,
  };
};

const FallCrawlPassport = ({
  event,
  stops = [],
  preview = false,
  storage,
  limitedSignal = false,
  revealMode = "legacy",
  collectionEnabled,
  mysteryMode = false,
  visitorLocation = null,
  adminTestMode = false,
  onResetAdminTestData = null,
}) => {
  const isLockedReveal = !preview && revealMode === "locked";
  const enabled = preview || ["locked", "unlocked-preview", "full"].includes(revealMode)
    ? isFallCrawlPassportIdentity(event || {})
    : isFallCrawlPassportEvent(event || {});
  const passportStops = useMemo(
    () => getFallCrawlPassportStops(event || {}, stops, {
      allowDisabledPreview: preview || revealMode === "full" || revealMode === "unlocked-preview",
    }),
    [event, preview, revealMode, stops]
  );
  const storageOptions = useMemo(
    () => adminTestMode ? { storageKeySuffix: "admin-test" } : {},
    [adminTestMode]
  );
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState(() => (
    enabled
      ? loadFallCrawlPassportState(event.id, storage, storageOptions)
      : createEmptyFallCrawlPassportState(event?.id || "")
  ));
  const [message, setMessage] = useState("");
  const [lockedResponse, setLockedResponse] = useState(false);
  const [reward, setReward] = useState(null);
  const [busyStopId, setBusyStopId] = useState("");
  const [isCoverOpening, setIsCoverOpening] = useState(false);
  const [lastEarnedStopId, setLastEarnedStopId] = useState("");
  const [justUnlockedCountyKey, setJustUnlockedCountyKey] = useState("");
  const [progressIsUpdating, setProgressIsUpdating] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [checkedLocation, setCheckedLocation] = useState(null);
  const [locationError, setLocationError] = useState("");
  const [giveawayPromptOpen, setGiveawayPromptOpen] = useState(false);
  const [giveawayPromptDeferred, setGiveawayPromptDeferred] = useState(false);
  const [giveawayForm, setGiveawayForm] = useState({ name: "", phone: "", email: "" });
  const [giveawayError, setGiveawayError] = useState("");
  const [giveawayBusy, setGiveawayBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [singlePageMode, setSinglePageMode] = useState(() => (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 720px)").matches
  ));
  const closeButtonRef = useRef(null);
  const coverTimerRef = useRef(null);
  const rewardTimerRef = useRef(null);
  const motionTimerRef = useRef(null);
  const giveawayTimerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    setState(loadFallCrawlPassportState(event.id, storage, storageOptions));
  }, [enabled, event?.id, storage, storageOptions]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.setTimeout(() => closeButtonRef.current?.focus(), 0);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("fall-crawl-passport-open");
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove("fall-crawl-passport-open");
    };
  }, [isOpen]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(max-width: 720px)");
    const updateSinglePageMode = () => setSinglePageMode(media.matches);
    updateSinglePageMode();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", updateSinglePageMode);
      return () => media.removeEventListener("change", updateSinglePageMode);
    }
    media.addListener(updateSinglePageMode);
    return () => media.removeListener(updateSinglePageMode);
  }, []);

  useEffect(() => () => {
    window.clearTimeout(coverTimerRef.current);
    window.clearTimeout(rewardTimerRef.current);
    window.clearTimeout(motionTimerRef.current);
    window.clearTimeout(giveawayTimerRef.current);
  }, []);

  useEffect(() => {
    if (!isOpen || giveawayPromptOpen || giveawayPromptDeferred) return;
    const currentStatus = getFallCrawlGiveawayStatus(state);
    if (!currentStatus.eligible || currentStatus.entrySubmitted || currentStatus.declined) return;
    window.clearTimeout(giveawayTimerRef.current);
    giveawayTimerRef.current = window.setTimeout(() => {
      setGiveawayError("");
      setGiveawayPromptOpen(true);
    }, prefersReducedMotion() ? 100 : 350);
  }, [
    giveawayPromptOpen,
    giveawayPromptDeferred,
    isOpen,
    state.giveawayDeclined,
    state.giveawayEntrySubmitted,
    state.verifiedStampCount,
    state,
  ]);

  if (!enabled) return null;

  const progress = getFallCrawlPassportProgress(passportStops, state);
  const giveawayStatus = getFallCrawlGiveawayStatus(state);
  const giveawayStateLabel = giveawayStatus.entrySubmitted
    ? "entered"
    : giveawayStatus.declined
      ? "declined"
      : "undecided";
  const countyProgress = getFallCrawlCountyProgress(passportStops, state);
  const collectionGate = collectionEnabled === undefined ? true : collectionEnabled === true;
  const canPublicCollect = preview
    ? true
    : adminTestMode
      ? true
      : collectionGate && canCollectFallCrawlPassportStamps(event || {});
  const hasConfiguredStops = passportStops.length > 0;
  const activeVisitorLocation = visitorLocation || checkedLocation;
  const showMysteryStops = mysteryMode && !preview;
  const displayProgress = showMysteryStops
    ? { collectedCount: 0, totalStops: 0, percent: 0, label: "Official stop stamps are waiting to be revealed" }
    : progress;
  const pageStep = singlePageMode ? 1 : 2;
  const collectionHelp = showMysteryStops
    ? "The Backroads Book opens for a first look, but official stop names and stamp collection are still waiting to be revealed."
    : hasConfiguredStops
      ? canPublicCollect
        ? "Stamp collection is open. Location is checked only when you intentionally collect a stamp."
        : "The Backroads Book: Fall Crawl 2026 stamp collection is not live yet."
      : "Backroads Book stops still need explicit county configuration before stamps can be collected.";

  const persistState = (nextState) => {
    const saved = saveFallCrawlPassportState(event.id, nextState, storage, storageOptions);
    setState(saved);
    return saved;
  };

  const updateGiveawayState = (updates = {}) => persistState({
    ...state,
    ...updates,
  });

  const openPassport = () => {
    if (isCoverOpening) return;
    if (isLockedReveal) {
      setLockedResponse(true);
      setMessage("The Backroads Book is still locked. You may want to check again soon...");
      window.clearTimeout(motionTimerRef.current);
      motionTimerRef.current = window.setTimeout(() => setLockedResponse(false), 900);
      return;
    }
    if (!preview && !adminTestMode && event?.id) {
      trackFallCrawlInteraction("backroads_book_open", {
        eventId: event.id,
        label: "Backroads Book",
        allowRepeat: true,
      });
    }
    setCurrentPageIndex(0);
    if (prefersReducedMotion()) {
      setIsOpen(true);
      return;
    }
    setIsCoverOpening(true);
    window.clearTimeout(coverTimerRef.current);
    coverTimerRef.current = window.setTimeout(() => {
      setIsOpen(true);
      setIsCoverOpening(false);
    }, 500);
  };

  const markProgressMotion = ({ stopId = "", countyKey = "" } = {}) => {
    setLastEarnedStopId(stopId);
    setJustUnlockedCountyKey(countyKey);
    setProgressIsUpdating(true);
    window.clearTimeout(motionTimerRef.current);
    motionTimerRef.current = window.setTimeout(() => {
      setLastEarnedStopId("");
      setJustUnlockedCountyKey("");
      setProgressIsUpdating(false);
    }, 1200);
  };

  const openGiveawayPrompt = () => {
    setGiveawayError("");
    setGiveawayPromptDeferred(false);
    setGiveawayPromptOpen(true);
  };

  const markGiveawayDismissed = () => {
    setGiveawayPromptOpen(false);
    setGiveawayPromptDeferred(true);
    setGiveawayError("");
    setMessage("Giveaway entry stays unlocked. You can enter from the Backroads Book when you are ready.");
  };

  const declineGiveaway = () => {
    updateGiveawayState({
      giveawayDeclined: true,
      giveawayPromptDismissed: true,
    });
    setGiveawayPromptOpen(false);
    setGiveawayPromptDeferred(false);
    setGiveawayError("");
    setMessage("Giveaway entry declined for this passport.");
  };

  const maybeTriggerGiveawayPrompt = (previousState, nextState) => {
    const previousStatus = getFallCrawlGiveawayStatus(previousState);
    const nextStatus = getFallCrawlGiveawayStatus(nextState);
    if (
      previousStatus.eligible ||
      !nextStatus.eligible ||
      nextStatus.entrySubmitted ||
      nextStatus.declined
    ) {
      return;
    }
    window.clearTimeout(giveawayTimerRef.current);
    giveawayTimerRef.current = window.setTimeout(() => {
      setGiveawayPromptDeferred(false);
      setReward({
        type: "giveaway",
        title: "YOU DID IT!",
        text: `${FALL_CRAWL_GIVEAWAY_STAMP_THRESHOLD} verified stops in your Backroads Book.`,
      });
      openGiveawayPrompt();
    }, prefersReducedMotion() ? 300 : GIVEAWAY_PROMPT_DELAY_MS);
  };

  const awardStop = (passportStop, { bypassLocation = false, serverClaim = null } = {}) => {
    const previousState = state;
    const result = collectFallCrawlPassportStop(passportStops, previousState, passportStop.stopId);
    const verifiedStampCount = Math.max(
      Number(previousState.verifiedStampCount || 0),
      Number(serverClaim?.verifiedStampCount || 0)
    );
    const saved = persistState({
      ...result.state,
      anonymousPassportId: previousState.anonymousPassportId,
      verifiedStampCount,
      giveawayPromptDismissed: previousState.giveawayPromptDismissed === true,
      giveawayEntrySubmitted: previousState.giveawayEntrySubmitted === true,
      giveawayDeclined: previousState.giveawayDeclined === true,
    });
    const nextReward = getRewardMessage(result, previousState, saved);
    const previousBadges = new Set(previousState.unlockedCountyBadges || []);
    const newCounty = (saved.unlockedCountyBadges || []).find((key) => !previousBadges.has(key)) || "";
    if (nextReward) {
      setReward(nextReward);
      window.clearTimeout(rewardTimerRef.current);
      rewardTimerRef.current = window.setTimeout(() => setReward(null), nextReward.type === "route" ? 3600 : 2400);
    }
    if (result.awarded) {
      markProgressMotion({ stopId: passportStop.stopId, countyKey: newCounty });
    }
    maybeTriggerGiveawayPrompt(previousState, saved);
    setMessage(result.reason === "duplicate"
      ? `${passportStop.displayName} is already in this passport.`
      : bypassLocation
        ? `${passportStop.displayName} added in admin test mode.`
        : limitedSignal
          ? "Stamp saved on this device."
          : `${passportStop.displayName} stamp collected.`);
    return result;
  };

  const recordVerifiedClaim = async (passportStop, visitorLocation) => {
    const callable = httpsCallable(getFunctions(), "recordFallCrawlPassportClaim");
    const result = await callable({
      eventId: event.id,
      anonymousPassportId: state.anonymousPassportId,
      stopId: passportStop.stopId,
      latitude: visitorLocation.lat,
      longitude: visitorLocation.lng,
      ...(adminTestMode ? { adminTestMode: true } : {}),
    });
    return result.data || {};
  };

  const checkLocation = async () => {
    setBusyStopId("location-check");
    setMessage("Checking your current location...");
    try {
      const position = await getPositionOnce();
      setCheckedLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      setLocationError("");
      setMessage("Location checked. Nearby stamps are claimable while you are close to a stop.");
    } catch (locationCheckError) {
      const nextMessage = getLocationErrorMessage(locationCheckError);
      setLocationError(nextMessage);
      setMessage(nextMessage);
    } finally {
      setBusyStopId("");
    }
  };

  const claimWithFreshLocation = async (passportStop) => {
    if (!canPublicCollect) {
      setMessage("The Backroads Book: Fall Crawl 2026 stamp collection is not live yet.");
      return;
    }
    if (!passportStop.coordinates) {
      setMessage("This stop cannot currently be location-verified. Please check with the event host.");
      return;
    }

    setBusyStopId(passportStop.stopId);
    setMessage("Checking your current location before claiming...");
    try {
      const position = await getPositionOnce();
      const visitorLocation = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      setCheckedLocation(visitorLocation);
      setLocationError("");
      if (!isWithinFallCrawlPassportRadius(
        visitorLocation,
        passportStop.coordinates,
        FALL_CRAWL_PASSPORT_CLAIM_RADIUS_METERS
      )) {
        setMessage("You are not close enough to this official stop yet. Move closer and try again.");
        return;
      }
      const serverClaim = await recordVerifiedClaim(passportStop, visitorLocation);
      awardStop(passportStop, { serverClaim });
    } catch (locationCheckError) {
      const nextMessage = typeof locationCheckError?.code === "number"
        ? getLocationErrorMessage(locationCheckError)
        : locationCheckError?.message || "This verified stamp could not be saved right now. Try again when your connection is steady.";
      setLocationError(nextMessage);
      setMessage(nextMessage);
    } finally {
      setBusyStopId("");
    }
  };

  const simulateStop = (passportStop) => awardStop(passportStop, { bypassLocation: true });

  const collectStops = (items, statusMessage) => {
    let nextState = state;
    items.forEach((item) => {
      nextState = collectFallCrawlPassportStop(passportStops, nextState, item.stopId).state;
    });
    const saved = persistState(nextState);
    const previousBadges = new Set(state.unlockedCountyBadges || []);
    const newCounty = (saved.unlockedCountyBadges || []).find((key) => !previousBadges.has(key)) || "";
    markProgressMotion({ stopId: items[items.length - 1]?.stopId || "", countyKey: newCounty });
    setReward(saved.routeComplete
      ? { type: "route", title: "THE BACKROADS BOOK ROUTE COMPLETE!", text: "Full test route unlocked." }
      : { type: "county", title: statusMessage, text: "Test badge state updated." });
    window.clearTimeout(rewardTimerRef.current);
    rewardTimerRef.current = window.setTimeout(() => setReward(null), saved.routeComplete ? 3600 : 2600);
    setMessage(statusMessage);
  };

  const resetPassport = async () => {
    const currentPassportId = state.anonymousPassportId;
    setResetBusy(true);
    try {
      if (adminTestMode && typeof onResetAdminTestData === "function") {
        await onResetAdminTestData(currentPassportId);
      }
      const next = resetFallCrawlPassportState(event.id, storage, storageOptions);
      setState(next);
      setReward(null);
      setLastEarnedStopId("");
      setJustUnlockedCountyKey("");
      setProgressIsUpdating(false);
      setGiveawayPromptOpen(false);
      setGiveawayPromptDeferred(false);
      setMessage(adminTestMode ? "Admin test passport reset." : "Backroads Book reset.");
    } catch (resetError) {
      setMessage(resetError?.message || "Admin test passport could not be reset.");
    } finally {
      setResetBusy(false);
    }
  };

  const openGiveawayFromBook = () => {
    setGiveawayError("");
    setGiveawayPromptDeferred(false);
    setGiveawayPromptOpen(true);
  };

  const submitGiveawayEntry = async (submitEvent) => {
    submitEvent.preventDefault();
    const name = giveawayForm.name.trim().replace(/\s+/g, " ");
    const phone = normalizeFallCrawlGiveawayPhone(giveawayForm.phone);
    const email = normalizeFallCrawlGiveawayEmail(giveawayForm.email);

    if (!name) {
      setGiveawayError("Please enter your name.");
      return;
    }
    if (!phone && !email) {
      setGiveawayError("Please enter a valid phone number or email address.");
      return;
    }

    setGiveawayBusy(true);
    setGiveawayError("");
    try {
      const callable = httpsCallable(getFunctions(), "submitFallCrawlGiveawayEntry");
      const result = await callable({
        eventId: event.id,
        anonymousPassportId: state.anonymousPassportId,
        name,
        phone,
        email,
        ...(adminTestMode ? { adminTestMode: true } : {}),
      });
      const data = result.data || {};
      const saved = updateGiveawayState({
        verifiedStampCount: Math.max(
          Number(state.verifiedStampCount || 0),
          Number(data.qualifiedStampCount || 0)
        ),
        giveawayEntrySubmitted: true,
        giveawayPromptDismissed: true,
        giveawayDeclined: false,
      });
      setState(saved);
      setGiveawayPromptOpen(false);
      setGiveawayPromptDeferred(false);
      setMessage(data.alreadyEntered
        ? "Giveaway entry already submitted."
        : "Your Fall Crawl giveaway entry has been received.");
    } catch (entryError) {
      setGiveawayError(entryError?.message || "Giveaway entry could not be submitted right now.");
    } finally {
      setGiveawayBusy(false);
    }
  };

  const clearStop = (stopId) => {
    const next = removeFallCrawlPassportStop(passportStops, state, stopId);
    persistState(next);
    setMessage("Stamp cleared.");
  };

  const stopPageGroups = passportStops.length > 0
    ? passportStops.reduce((groups, passportStop, index) => {
      const groupIndex = Math.floor(index / 2);
      if (!groups[groupIndex]) groups[groupIndex] = [];
      groups[groupIndex].push(passportStop);
      return groups;
    }, [])
    : [[]];

  const introPage = {
    key: "intro",
    className: "identity",
    content: (
      <>
        <img className="fall-crawl-passport-header-art" src={PASSPORT_ASSETS.header} alt="The Backroads Book: Fall Crawl 2026" decoding="async" />
        <p className="fall-crawl-eyebrow">Keepsake route book</p>
        <h3 id="fall-crawl-passport-dialog-title">The Backroads Book: Fall Crawl 2026</h3>
        <FallCrawlProgressBar progress={displayProgress} isUpdating={progressIsUpdating} />
        {giveawayStatus.entrySubmitted ? (
          <p className="fall-crawl-passport-giveaway-status">Giveaway Entry Submitted</p>
        ) : giveawayStatus.declined ? (
          <p className="fall-crawl-passport-giveaway-status">Giveaway Declined</p>
        ) : giveawayStatus.eligible ? (
          <button type="button" className="fall-crawl-passport-action secondary" onClick={openGiveawayFromBook}>
            Enter Giveaway
          </button>
        ) : (
          <p className="fall-crawl-passport-help">
            {FALL_CRAWL_GIVEAWAY_STAMP_THRESHOLD} verified stop stamps unlock optional giveaway entry.
          </p>
        )}
        <p className="fall-crawl-passport-help">{collectionHelp}</p>
      </>
    ),
  };

  const routePage = {
    key: "route-progress",
    className: "route",
    content: (
      <>
        <div className="fall-crawl-passport-page-heading">
          <img src={PASSPORT_ASSETS.header} alt="" aria-hidden="true" loading="lazy" decoding="async" />
          <h3>Route progress</h3>
        </div>
        <p className="fall-crawl-passport-help">
          Visit official Fall Crawl stops in any order. Each collected stamp moves this keepsake book toward county and route rewards.
        </p>
        <div className="fall-crawl-passport-route-summary">
          <strong>{displayProgress.label}</strong>
          <span>{displayProgress.percent}% complete</span>
          <span>{giveawayStatus.verifiedStampCount} verified toward giveaway eligibility</span>
        </div>
      </>
    ),
  };

  const stopPages = stopPageGroups
    .map((pageStops, pageIndex) => ({
      key: `route-stamps-${pageIndex + 1}`,
      className: "stamps",
      content: (
        <>
          <div className="fall-crawl-passport-page-heading">
            <img src={PASSPORT_ASSETS.header} alt="" aria-hidden="true" loading="lazy" decoding="async" />
            <h3>Official stop stamps</h3>
          </div>
          {!canPublicCollect && (
            <p className="fall-crawl-passport-help">The Backroads Book: Fall Crawl 2026 stamp collection is not live yet.</p>
          )}
          {showMysteryStops ? (
            <div className="fall-crawl-passport-stamp-grid" aria-label="Mystery official stop stamps">
              {[0, 1, 2].map((slot) => (
                <article className="fall-crawl-passport-stamp locked mystery" key={`mystery-${slot}`}>
                  <div className="fall-crawl-passport-stamp-art" aria-label="Official stop waiting to be revealed">
                    <img
                      className="fall-crawl-passport-stamp-image locked"
                      src={PASSPORT_ASSETS.lockedStamp}
                      alt="Official stop stamp locked"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div>
                    <h4>Official Stop</h4>
                    <p>Waiting to be revealed.</p>
                  </div>
                </article>
              ))}
            </div>
          ) : pageStops.length === 0 ? (
            <p className="fall-crawl-passport-empty">No Backroads Book official stops are configured yet.</p>
          ) : (
            <div className="fall-crawl-passport-stamp-grid">
              {pageStops.map((passportStop) => (
                <FallCrawlPassportStamp
                  key={passportStop.stopId}
                  passportStop={passportStop}
                  collectedAt={state.collectedStops[passportStop.stopId]}
                  canCollect={canPublicCollect}
                  busy={busyStopId === passportStop.stopId || busyStopId === "location-check"}
                  isNew={lastEarnedStopId === passportStop.stopId}
                  nearby={Boolean(activeVisitorLocation && passportStop.coordinates && isWithinFallCrawlPassportRadius(
                    activeVisitorLocation,
                    passportStop.coordinates,
                    FALL_CRAWL_PASSPORT_CLAIM_RADIUS_METERS
                  ))}
                  locationAvailable={Boolean(activeVisitorLocation)}
                  locationError={locationError}
                  onCheckLocation={checkLocation}
                  onClaim={claimWithFreshLocation}
                />
              ))}
            </div>
          )}
        </>
      ),
    }));

  const rewardPage = {
    key: "rewards",
    className: "rewards",
    content: (
      <>
        <div className="fall-crawl-passport-page-heading">
          <img src={PASSPORT_ASSETS.header} alt="" aria-hidden="true" loading="lazy" decoding="async" />
          <h3>Completion rewards</h3>
        </div>
        <div className="fall-crawl-passport-county-grid">
          {countyProgress.map((county) => (
            <FallCrawlCountyBadge
              key={county.key}
              county={county}
              isNew={justUnlockedCountyKey === county.key}
            />
          ))}
        </div>
        <article className={`fall-crawl-passport-route-complete ${state.routeComplete ? "complete" : "locked"}`}>
          <img
            className="fall-crawl-passport-route-badge"
            src={PASSPORT_ASSETS.routeCompleteBadge}
            alt={state.routeComplete ? "The Backroads Book: Fall Crawl 2026 route complete badge unlocked" : ""}
            aria-hidden={state.routeComplete ? undefined : "true"}
            loading="lazy"
            decoding="async"
          />
          <div>
            <span>Full Route Badge</span>
            <strong>{state.routeComplete ? "The Backroads Book: Fall Crawl 2026 - Route Complete" : "Complete the full official route to unlock this badge."}</strong>
          </div>
          {state.routeComplete && <img className="fall-crawl-passport-confetti" src={PASSPORT_ASSETS.confettiOverlay} alt="" aria-hidden="true" loading="lazy" decoding="async" />}
        </article>
        {giveawayStatus.entrySubmitted ? (
          <p className="fall-crawl-passport-giveaway-status">Giveaway Entry Submitted</p>
        ) : giveawayStatus.declined ? (
          <p className="fall-crawl-passport-giveaway-status">Giveaway Declined</p>
        ) : giveawayStatus.eligible ? (
          <button type="button" className="fall-crawl-passport-action" onClick={openGiveawayFromBook}>
            Enter Giveaway
          </button>
        ) : (
          <p className="fall-crawl-passport-help">
            Collect {FALL_CRAWL_GIVEAWAY_STAMP_THRESHOLD} server-verified stamps to unlock optional giveaway entry.
          </p>
        )}
      </>
    ),
  };

  const adminPage = preview ? {
    key: "admin-test",
    className: "admin",
    content: (
      <aside className="fall-crawl-passport-test-panel" aria-label="Admin passport test mode">
        <div>
          <p className="fall-crawl-eyebrow">{adminTestMode ? "ADMIN TEST MODE — NOT PUBLIC" : "Admin Test Mode"}</p>
          <h3>{adminTestMode ? "Backroads Book test passport" : "Backroads Book simulator"}</h3>
          <p>
            {adminTestMode
              ? "Admin-only test mode uses the real stamp and giveaway callables with isolated test records."
              : "Preview-only controls. Public visitors cannot see or use this panel."}
          </p>
          {adminTestMode && (
            <div className="fall-crawl-passport-route-summary">
              <strong>Current test stamp count: {giveawayStatus.verifiedStampCount}</strong>
              <span>Giveaway state: {giveawayStateLabel}</span>
            </div>
          )}
        </div>
        <div className="fall-crawl-passport-test-actions">
          {adminTestMode ? (
            <button type="button" onClick={resetPassport} disabled={resetBusy}>
              {resetBusy ? "Resetting..." : "RESET TEST PASSPORT"}
            </button>
          ) : (
            <>
              {passportStops.map((passportStop) => (
                <button type="button" key={passportStop.stopId} onClick={() => simulateStop(passportStop)}>
                  Award {passportStop.displayName}
                </button>
              ))}
              <button type="button" onClick={() => collectStops(
                passportStops.filter((item) => item.countyKey === "bedford"),
                "BEDFORD COUNTY COMPLETE!"
              )}>
                Simulate Bedford completion
              </button>
              <button type="button" onClick={() => collectStops(
                passportStops.filter((item) => item.countyKey === "coffee"),
                "COFFEE COUNTY COMPLETE!"
              )}>
                Simulate Coffee completion
              </button>
              <button type="button" onClick={() => collectStops(passportStops, "Full route complete.")}>
                Simulate full route completion
              </button>
              {Object.keys(state.collectedStops).map((stopId) => (
                <button type="button" key={`clear-${stopId}`} onClick={() => clearStop(stopId)}>
                  Clear {passportStops.find((item) => item.stopId === stopId)?.displayName || stopId}
                </button>
              ))}
              <button type="button" onClick={resetPassport}>Reset test passport</button>
            </>
          )}
        </div>
        <pre>{JSON.stringify(state, null, 2)}</pre>
      </aside>
    ),
  } : null;

  const passportPages = [introPage, routePage, ...stopPages, rewardPage, adminPage].filter(Boolean);
  const visiblePages = passportPages.slice(currentPageIndex, currentPageIndex + pageStep);
  const currentPageNumber = currentPageIndex + 1;
  const hasPreviousPage = currentPageIndex > 0;
  const hasNextPage = currentPageIndex + pageStep < passportPages.length;

  const turnToPreviousPage = () => {
    setCurrentPageIndex((index) => Math.max(0, index - pageStep));
  };

  const turnToNextPage = () => {
    setCurrentPageIndex((index) => Math.min(passportPages.length - 1, index + pageStep));
  };

  const closePassport = () => setIsOpen(false);

  return (
    <section className="fall-crawl-passport-section" aria-labelledby="fall-crawl-passport-title">
      <div className={`fall-crawl-passport-entry ${isCoverOpening ? "is-opening" : ""} ${lockedResponse ? "is-locked-response" : ""}`}>
        <button
          type="button"
          className="fall-crawl-passport-cover"
          onClick={openPassport}
          disabled={isCoverOpening}
          aria-label="Open The Backroads Book: Fall Crawl 2026"
        >
          <img src={PASSPORT_ASSETS.cover} alt="" loading="lazy" decoding="async" aria-hidden="true" />
        </button>
        <div className="fall-crawl-passport-entry-copy">
          <p className="fall-crawl-eyebrow">{isLockedReveal ? "THE BACKROADS BOOK" : "The Backroads Book"}</p>
          <h2 id="fall-crawl-passport-title">The Backroads Book: Fall Crawl 2026</h2>
          <p>{isLockedReveal ? "The keepsake route book is almost ready to open." : "Collect official-stop stamps, unlock county badges, and complete the full route."}</p>
          {!isLockedReveal && <FallCrawlProgressBar progress={displayProgress} isUpdating={progressIsUpdating} />}
          <button type="button" className="fall-crawl-button secondary" onClick={openPassport} disabled={isCoverOpening}>
            {isLockedReveal ? "CHECK THE LOCK" : "Open Backroads Book"}
          </button>
          {message && !isOpen && <p className="fall-crawl-passport-message" role="status">{message}</p>}
        </div>
      </div>

      {isOpen && (
        <div className="fall-crawl-passport-overlay" role="dialog" aria-modal="true" aria-labelledby="fall-crawl-passport-dialog-title">
          <div
            className="fall-crawl-passport-book"
            style={{ "--fall-crawl-passport-page-bg": `url(${PASSPORT_ASSETS.pageBg})` }}
          >
            <button
              type="button"
              className="fall-crawl-passport-close"
              onClick={closePassport}
              ref={closeButtonRef}
            >
              Close
            </button>
            <div className={`fall-crawl-passport-spread ${singlePageMode ? "single-page" : "two-page"}`} key={`${currentPageIndex}-${pageStep}`}>
              {visiblePages.map((page, pageOffset) => (
                <div
                  className={`fall-crawl-passport-page ${page.className} ${pageOffset === 0 ? "left-page" : "right-page"}`}
                  key={page.key}
                >
                  {page.content}
                </div>
              ))}
            </div>
            {message && <p className="fall-crawl-passport-message" role="status">{message}</p>}
            <div className="fall-crawl-passport-pagination" aria-label="Backroads Book page navigation">
              <button type="button" onClick={turnToPreviousPage} disabled={!hasPreviousPage}>
                Previous Page
              </button>
              <span>
                Page {currentPageNumber}{!singlePageMode && visiblePages.length > 1 ? `-${currentPageNumber + visiblePages.length - 1}` : ""} of {passportPages.length}
              </span>
              <button type="button" onClick={turnToNextPage} disabled={!hasNextPage}>
                Next Page
              </button>
            </div>
          </div>
          {reward && (
            <div className={`fall-crawl-passport-reward ${reward.type}`} role="status">
              {reward.type === "route" && (
                <span className="fall-crawl-passport-leaf-burst" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                </span>
              )}
              {reward.type !== "stamp" && <img className="fall-crawl-passport-reward-confetti" src={PASSPORT_ASSETS.confettiOverlay} alt="" aria-hidden="true" decoding="async" />}
              <img
                className="fall-crawl-passport-reward-banner"
                src={reward.type === "stamp"
                  ? PASSPORT_ASSETS.stampCollectedBanner
                  : reward.type === "route"
                    ? PASSPORT_ASSETS.routeCompleteBadge
                    : PASSPORT_ASSETS.countyCompleteBanner}
                alt=""
                aria-hidden="true"
                decoding="async"
              />
              <strong>{reward.title}</strong>
              <span>{reward.text}</span>
            </div>
          )}
          {giveawayPromptOpen && (
            <aside className="fall-crawl-passport-giveaway" aria-label="Fall Crawl giveaway entry">
              <img src={PASSPORT_ASSETS.confettiOverlay} alt="" aria-hidden="true" loading="lazy" decoding="async" />
              <div>
                <p className="fall-crawl-eyebrow">Fall Crawl Giveaway</p>
                <h3>YOU DID IT!</h3>
                <p>
                  Five stops in your Backroads Book. You have unlocked an optional entry into the Fall Crawl Giveaway.
                </p>
              </div>
              <form onSubmit={submitGiveawayEntry}>
                <label>Name<input value={giveawayForm.name} onChange={(inputEvent) => setGiveawayForm((current) => ({ ...current, name: inputEvent.target.value }))} /></label>
                <label>Phone<input value={giveawayForm.phone} onChange={(inputEvent) => setGiveawayForm((current) => ({ ...current, phone: inputEvent.target.value }))} /></label>
                <label>Email<input value={giveawayForm.email} onChange={(inputEvent) => setGiveawayForm((current) => ({ ...current, email: inputEvent.target.value }))} /></label>
                <p>We will only use this contact information to reach you about the Fall Crawl giveaway.</p>
                {giveawayError && <p className="fall-crawl-passport-message" role="alert">{giveawayError}</p>}
                <div className="fall-crawl-passport-giveaway-actions">
                  <button type="submit" className="fall-crawl-passport-action" disabled={giveawayBusy}>
                    {giveawayBusy ? "Entering..." : "Enter Giveaway"}
                  </button>
                  <button type="button" className="fall-crawl-passport-action secondary" disabled={giveawayBusy} onClick={markGiveawayDismissed}>
                    Maybe later
                  </button>
                  <button type="button" className="fall-crawl-passport-action secondary" disabled={giveawayBusy} onClick={declineGiveaway}>
                    Decline
                  </button>
                </div>
              </form>
            </aside>
          )}
        </div>
      )}
    </section>
  );
};

export default FallCrawlPassport;
